<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { router, usePage } from '@inertiajs/vue3';
import { debounce } from 'lodash';
import AdminLayout from "@/Layouts/AdminLayout/AdminLayout.vue";
import AdminPagination from "@/Components/AdminPagination.vue";
import { useToast } from "@/composables/useToast.js";
import { useSettings } from "@/composables/useSettings.js";
import useCurrency from "@/composables/useCurrency.js";

const props = defineProps({
    transactions: {
        type: Object,
        required: true,
        default: () => ({ data: [] }),
        validator: (value) => value && typeof value === 'object' && Array.isArray(value.data)
    },
    transactionTypes: {
        type: Array,
        default: () => ['deposit', 'withdrawal', 'transfer', 'credit', 'debit', 'bonus', 'stake', 'unstake', 'staking_reward'],
        validator: (value) => Array.isArray(value)
    },
    statuses: {
        type: Array,
        default: () => ['pending', 'completed', 'failed', 'cancelled'],
        validator: (value) => Array.isArray(value)
    },
    filters: {
        type: Object,
        default: () => ({
            sort_field: 'created_at',
            sort_direction: 'desc'
        }),
        validator: (value) => value && typeof value === 'object'
    },
    meta: {
        type: Object,
        default: () => ({
            total: 0,
            current_page: 1,
            per_page: 20,
            last_page: 1
        }),
        validator: (value) => {
            return value && typeof value === 'object' &&
                typeof value.total === 'number' &&
                typeof value.current_page === 'number' &&
                typeof value.per_page === 'number' &&
                typeof value.last_page === 'number';
        }
    },
    stats: {
        type: Object,
        default: () => ({
            total_transactions: 0,
            total_amount: 0,
            total_fees: 0,
            transactions_by_status: {},
            transactions_by_type: {},
            recent_transactions: 0,
            today_transactions: 0,
            this_month_transactions: 0,
            completed_transactions: 0,
            pending_transactions: 0
        }),
        validator: (value) => value && typeof value === 'object'
    }
});

const { showToast } = useToast();
const { currencySymbol } = useSettings();
const { currencySymbols } = useCurrency();
const isLoading = ref(false);
const error = ref(null);
const showDetailsModal = ref(false);
const selectedTransaction = ref(null);
const searchTerm = ref(props.filters?.search || '');
const selectedType = ref(props.filters?.type || '');
const selectedStatus = ref(props.filters?.status || '');
const selectedStartDate = ref(props.filters?.start_date || '');
const selectedEndDate = ref(props.filters?.end_date || '');
const currentPage = computed(() => props.meta.current_page || 1);
const lastPage = computed(() => props.meta.last_page || 1);
const sortField = computed(() => props.filters.sort_field || 'created_at');
const sortDirection = computed(() => props.filters.sort_direction || 'desc');
const hasActiveFilters = computed(() => {
    return !!(searchTerm.value || selectedType.value || selectedStatus.value || selectedStartDate.value || selectedEndDate.value);
});

const debouncedSearch = debounce(() => {
    applyFilters();
}, 500);

const applyFilters = () => {
    if (isLoading.value) return;

    isLoading.value = true;
    error.value = null;

    const params = {
        page: 1,
        sort_field: sortField.value,
        sort_direction: sortDirection.value
    };

    if (searchTerm.value?.trim()) params.search = searchTerm.value.trim();
    if (selectedType.value) params.type = selectedType.value;
    if (selectedStatus.value) params.status = selectedStatus.value;
    if (selectedStartDate.value) params.start_date = selectedStartDate.value;
    if (selectedEndDate.value) params.end_date = selectedEndDate.value;

    router.get('/admin/transactions', params, {
        preserveState: true,
        preserveScroll: true,
        replace: true,
        onFinish: () => {
            isLoading.value = false;
        },
        onError: (errors) => {
            isLoading.value = false;
            console.error('Filter error:', errors);
            error.value = 'Failed to apply filters. Please try again.';
            showToast('Failed to apply filters', 'error');
        }
    });
};

const clearSearch = () => {
    searchTerm.value = '';
    applyFilters();
};

const clearTypeFilter = () => {
    selectedType.value = '';
    applyFilters();
};

const clearStatusFilter = () => {
    selectedStatus.value = '';
    applyFilters();
};

const clearDateFilter = () => {
    selectedStartDate.value = '';
    selectedEndDate.value = '';
    applyFilters();
};

const clearAllFilters = () => {
    searchTerm.value = '';
    selectedType.value = '';
    selectedStatus.value = '';
    selectedStartDate.value = '';
    selectedEndDate.value = '';

    isLoading.value = true;
    error.value = null;

    router.get('/admin/transactions', {
        sort_field: 'created_at',
        sort_direction: 'desc'
    }, {
        preserveState: true,
        preserveScroll: true,
        replace: true,
        onFinish: () => {
            isLoading.value = false;
            showToast('Filters cleared successfully', 'success');
        },
        onError: (errors) => {
            isLoading.value = false;
            console.error('Clear filters error:', errors);
            error.value = 'Failed to clear filters. Please try again.';
            showToast('Failed to clear filters', 'error');
        }
    });
};

const showTransactionDetails = (transaction) => {
    selectedTransaction.value = transaction;
    showDetailsModal.value = true;
};

const closeDetailsModal = () => {
    showDetailsModal.value = false;
    selectedTransaction.value = null;
};

const formatNumber = (value) => {
    if (value === null || value === undefined) return '0';
    const num = parseFloat(value) || 0;
    return new Intl.NumberFormat('en-US').format(num);
};

const getInitials = (name) => {
    if (!name || typeof name !== 'string') return '?';
    return name.split(' ').map(n => n.charAt(0)).join('').toUpperCase().slice(0, 2);
};

const capitalize = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
};

const getCurrencyCode = (currency) => {
    if (!currency || typeof currency !== 'string') return '';
    return currency.trim().toUpperCase();
};

const getCurrencySymbol = (currency) => {
    const code = getCurrencyCode(currency);

    if (!code) {
        return currencySymbol.value;
    }

    return currencySymbols[code] || `${code} `;
};

const getCurrencyPrecision = (currency) => {
    const precisionMap = {
        BTC: 8,
        ETH: 8,
        BCH: 8,
        LTC: 8,
        XRP: 8,
        ADA: 8,
        DOT: 8,
        BNB: 8,
        SOL: 8,
        AVAX: 8,
        MATIC: 8,
        TRX: 8,
        USDT: 6,
        USDC: 6,
        BUSD: 6,
        DAI: 6,
        TUSD: 6,
        USDP: 6,
        FRAX: 6,
        LUSD: 6,
        USDD: 6,
    };

    return precisionMap[getCurrencyCode(currency)] ?? 2;
};

const formatMoney = (value, currency = null) => {
    if (value === null || value === undefined || value === '' || isNaN(value)) {
        return '0.00';
    }

    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
        return '0.00';
    }

    const absValue = Math.abs(numValue);

    if (absValue >= 1000000000) {
        return (absValue / 1000000000).toFixed(2) + 'B';
    } else if (absValue >= 1000000) {
        return (absValue / 1000000).toFixed(2) + 'M';
    } else if (absValue >= 1000) {
        return (absValue / 1000).toFixed(2) + 'K';
    } else {
        return absValue.toFixed(getCurrencyPrecision(currency));
    }
};

const formatAmount = (amount, type, currency = null) => {
    if (amount === null || amount === undefined || amount === '' || isNaN(amount)) {
        return `0.${'0'.repeat(getCurrencyPrecision(currency))}`;
    }

    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) {
        return `0.${'0'.repeat(getCurrencyPrecision(currency))}`;
    }

    let prefix = '';

    if (type === 'withdrawal' && numAmount > 0) {
        prefix = '-';
    } else if ((type === 'deposit' || type === 'credit' || type === 'bonus' || type === 'staking_reward') && numAmount > 0) {
        prefix = '+';
    }

    return `${prefix}${Math.abs(numAmount).toFixed(getCurrencyPrecision(currency))}`;
};

const getRawBtcFromDetails = (details) => {
    if (!details || typeof details !== 'string') return null;
    const match = details.match(/Raw BTC:\s*([0-9.]+)/i);
    if (!match) return null;
    const value = parseFloat(match[1]);
    return Number.isNaN(value) ? null : value;
};

const formatAmountWithDetails = (amount, type, currency = null, details = null) => {
    if (amount === null || amount === undefined || amount === '' || isNaN(amount)) {
        return `0.${'0'.repeat(getCurrencyPrecision(currency))}`;
    }

    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) {
        return `0.${'0'.repeat(getCurrencyPrecision(currency))}`;
    }

    let prefix = '';

    if (type === 'withdrawal' && numAmount > 0) {
        prefix = '-';
    } else if ((type === 'deposit' || type === 'credit' || type === 'bonus' || type === 'staking_reward') && numAmount > 0) {
        prefix = '+';
    }

    if ((currency || '').toUpperCase() === 'BTC') {
        const rawBtc = getRawBtcFromDetails(details);
        const precision = rawBtc !== null ? 12 : getCurrencyPrecision(currency);
        const value = rawBtc !== null ? rawBtc : numAmount;
        return `${prefix}${Math.abs(value).toFixed(precision)}`;
    }

    return `${prefix}${Math.abs(numAmount).toFixed(getCurrencyPrecision(currency))}`;
};

const getAmountColor = (amount, type) => {
    if (type === 'deposit' || type === 'credit' || type === 'bonus' || type === 'staking_reward') return 'text-green-600 dark:text-green-400';
    if (type === 'withdrawal' || type === 'debit' || type === 'stake') return 'text-red-600 dark:text-red-400';
    return 'text-gray-900 dark:text-gray-100';
};

// Date formatting functions
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    } catch (error) {
        console.error('Date formatting error:', error);
        return 'Invalid Date';
    }
};

const formatTime = (dateString) => {
    if (!dateString) return '';
    try {
        return new Date(dateString).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch (error) {
        console.error('Time formatting error:', error);
        return '';
    }
};

const parseTransactionDetails = (details, detailsData = null) => {
    if (detailsData && typeof detailsData === 'object') return detailsData;
    if (!details) return null;
    if (typeof details === 'object' && details !== null) return details;
    if (typeof details !== 'string') return null;

    try {
        const parsed = JSON.parse(details);
        return (parsed && typeof parsed === 'object') ? parsed : null;
    } catch {
        return null;
    }
};

const toTitleCase = (value) => {
    if (!value || typeof value !== 'string') return '';
    return value
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

const detailLabelMap = {
    referral_uid: 'Referral UID',
    referral_id: 'Referral ID',
};

const formatDetailValue = (key, value) => {
    if (value === null || value === undefined || value === '') return 'N/A';

    if (key === 'subtype' && typeof value === 'string') {
        const subtypeMap = {
            direct_referral_bonus: 'Direct Referral Bonus',
            binary_pairing_bonus: 'Binary Pairing Bonus',
            token_credit_allocation: 'DS-Credits Allocation',
        };
        return subtypeMap[value] || toTitleCase(value);
    }

    if (['package_amount', 'matched_volume', 'weaker_leg_volume', 'reward_amount', 'requested_reward', 'left_available', 'right_available'].includes(key)) {
        return `${currencySymbol.value}${parseFloat(value || 0).toFixed(2)}`;
    }

    if (['bonus_percentage', 'reward_percentage'].includes(key)) {
        const num = parseFloat(value);
        return Number.isNaN(num) ? `${value}%` : `${num}%`;
    }

    if (key.endsWith('_at') && typeof value === 'string') {
        return `${formatDate(value)} ${formatTime(value)}`.trim();
    }

    return String(value);
};

const formattedSelectedDetails = computed(() => {
    const parsed = parseTransactionDetails(
        selectedTransaction.value?.details,
        selectedTransaction.value?.details_data,
    );

    if (!parsed) return [];

    const hasReferralUid = Object.prototype.hasOwnProperty.call(parsed, 'referral_uid')
        && parsed.referral_uid !== null
        && parsed.referral_uid !== '';

    const keyOrder = [
        'subtype',
        'description',
        'referral_name',
        'referral_uid',
        'referral_id',
        'package_amount',
        'bonus_percentage',
        'weaker_leg',
        'left_available',
        'right_available',
        'matched_volume',
        'weaker_leg_volume',
        'reward_percentage',
        'requested_reward',
        'reward_amount',
        'declining_protection',
        'tier',
    ];

    const keys = [
        ...keyOrder.filter((key) => Object.prototype.hasOwnProperty.call(parsed, key)),
        ...Object.keys(parsed).filter((key) => !keyOrder.includes(key)),
    ].filter((key) => !(hasReferralUid && key === 'referral_id'));

    return keys.map((key) => ({
        key,
        label: detailLabelMap[key] || toTitleCase(key),
        value: formatDetailValue(key, parsed[key]),
    }));
});

const getStatusClass = (status) => {
    const classes = {
        'completed': 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700',
        'pending': 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-700',
        'failed': 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700',
        'cancelled': 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900/30 dark:text-gray-300 dark:border-gray-700'
    };
    return classes[status] || classes.completed;
};

const getStatusIcon = (status) => {
    const icons = {
        'completed': `<svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                        </svg>`,
        'pending': `<svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>`,
        'failed': `<svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                        </svg>`,
        'cancelled': `<svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636"></path>
                        </svg>`
    };
    return icons[status] || '';
};

const getTypeIcon = (type) => {
    const icons = {
        'deposit': `<svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
                        </svg>`,
        'withdrawal': `<svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4"></path>
                        </svg>`,
        'transfer': `<svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"></path>
                        </svg>`,
        'credit': `<svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
                        </svg>`,
        'debit': `<svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4"></path>
                        </svg>`,
        'bonus': `<svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"></path>
                        </svg>`,
        'stake': `<svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                        </svg>`,
        'unstake': `<svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z"></path>
                        </svg>`,
        'staking_reward': `<svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"></path>
                        </svg>`
    };
    return icons[type] || '';
};

const getTypeBadgeClass = (type) => {
    const classes = {
        'deposit': 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700',
        'withdrawal': 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700',
        'transfer': 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700',
        'credit': 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700',
        'debit': 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700',
        'bonus': 'bg-pink-50 text-pink-700 border-pink-200 dark:bg-pink-900/30 dark:text-pink-300 dark:border-pink-700',
        'stake': 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-700',
        'unstake': 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700',
        'staking_reward': 'bg-cyan-50 text-cyan-700 border-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-300 dark:border-cyan-700',
    };
    return classes[type] || 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900/30 dark:text-gray-300 dark:border-gray-700';
};

const handleSort = (field) => {
    if (isLoading.value) return;

    const currentSortField = props.filters.sort_field;
    const currentSortDirection = props.filters.sort_direction;

    let newDirection = 'asc';
    if (currentSortField === field && currentSortDirection === 'asc') {
        newDirection = 'desc';
    }

    isLoading.value = true;
    error.value = null;

    const params = {
        sort_field: field,
        sort_direction: newDirection,
        page: 1
    };

    if (searchTerm.value?.trim()) params.search = searchTerm.value.trim();
    if (selectedType.value) params.type = selectedType.value;
    if (selectedStatus.value) params.status = selectedStatus.value;
    if (selectedStartDate.value) params.start_date = selectedStartDate.value;
    if (selectedEndDate.value) params.end_date = selectedEndDate.value;

    router.get('/admin/transactions', params, {
        preserveState: true,
        preserveScroll: true,
        replace: true,
        onFinish: () => {
            isLoading.value = false;
        },
        onError: (errors) => {
            isLoading.value = false;
            console.error('Sort error:', errors);
            error.value = 'Failed to sort transactions. Please try again.';
            showToast('Failed to sort transactions', 'error');
        }
    });
};

const changePage = (page) => {
    if (isLoading.value || page === currentPage.value) return;

    isLoading.value = true;
    error.value = null;

    const params = { page };
    if (searchTerm.value?.trim()) params.search = searchTerm.value.trim();
    if (selectedType.value) params.type = selectedType.value;
    if (selectedStatus.value) params.status = selectedStatus.value;
    if (selectedStartDate.value) params.start_date = selectedStartDate.value;
    if (selectedEndDate.value) params.end_date = selectedEndDate.value;
    if (sortField.value) params.sort_field = sortField.value;
    if (sortDirection.value) params.sort_direction = sortDirection.value;

    router.get('/admin/transactions', params, {
        preserveState: true,
        preserveScroll: true,
        replace: true,
        onFinish: () => {
            isLoading.value = false;
        },
        onError: (errors) => {
            isLoading.value = false;
            console.error('Pagination error:', errors);
            error.value = 'Failed to load page. Please try again.';
            showToast('Failed to load page', 'error');
        }
    });
};

onMounted(() => {
    const page = usePage();
    const flash = page.props.flash;
    if (flash?.success) showToast(flash.success, 'success');
    if (flash?.error) {
        error.value = flash.error;
        showToast(flash.error, 'error');
    }
    if (flash?.warning) showToast(flash.warning, 'warning');
    if (flash?.info) showToast(flash.info, 'info');
});

onUnmounted(() => {
    showDetailsModal.value = false;
    selectedTransaction.value = null;
});
</script>


<template>
    <AdminLayout
        title="Transaction Management"
        page-section="Finance & Transactions"
    >
        <div class="mb-6">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 class="text-2xl font-bold text-gray-900 dark:text-white">
                        Transaction Management
                    </h1>
                    <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                        Monitor and manage all financial transactions
                    </p>
                </div>
            </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
                <div class="flex items-center">
                    <div class="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                        <svg
                            class="h-6 w-6 text-blue-600 dark:text-blue-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                            />
                        </svg>
                    </div>
                    <div class="ml-4">
                        <p class="text-sm font-medium text-gray-600 dark:text-gray-400">
                            Total Transactions
                        </p>
                        <p class="text-2xl font-bold text-gray-900 dark:text-white">
                            {{ formatNumber(stats.total_transactions) }}
                        </p>
                    </div>
                </div>
            </div>

            <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
                <div class="flex items-center">
                    <div class="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                        <svg
                            class="h-6 w-6 text-green-600 dark:text-green-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                        </svg>
                    </div>
                    <div class="ml-4">
                        <p class="text-sm font-medium text-gray-600 dark:text-gray-400">
                            Total Volume
                        </p>
                        <p class="text-2xl font-bold text-gray-900 dark:text-white">
                            {{ currencySymbol }}{{ formatMoney(stats.total_amount) }}
                        </p>
                    </div>
                </div>
            </div>

            <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
                <div class="flex items-center">
                    <div class="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                        <svg
                            class="h-6 w-6 text-purple-600 dark:text-purple-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                        </svg>
                    </div>
                    <div class="ml-4">
                        <p class="text-sm font-medium text-gray-600 dark:text-gray-400">
                            This Month
                        </p>
                        <p class="text-2xl font-bold text-gray-900 dark:text-white">
                            {{ formatNumber(stats.this_month_transactions) }}
                        </p>
                    </div>
                </div>
            </div>

            <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
                <div class="flex items-center">
                    <div class="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                        <svg
                            class="h-6 w-6 text-emerald-600 dark:text-emerald-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M5 13l4 4L19 7"
                            />
                        </svg>
                    </div>
                    <div class="ml-4">
                        <p class="text-sm font-medium text-gray-600 dark:text-gray-400">
                            Completed
                        </p>
                        <p class="text-2xl font-bold text-gray-900 dark:text-white">
                            {{ formatNumber(stats.completed_transactions) }}
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 mb-6">
            <div class="p-6">
                <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div class="flex-1 max-w-md">
                        <div class="relative">
                            <svg
                                class="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                />
                            </svg>
                            <input
                                id="search-transactions"
                                v-model="searchTerm"
                                type="text"
                                placeholder="Search by ID, amount, or user..."
                                class="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                                @input="debouncedSearch"
                            >
                            <button
                                v-if="searchTerm"
                                class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                @click="clearSearch"
                            >
                                <svg
                                    class="h-4 w-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </button>
                        </div>
                    </div>

                    <div class="flex flex-wrap items-center gap-3">
                        <div class="min-w-[140px]">
                            <select
                                v-model="selectedType"
                                class="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100"
                                @change="applyFilters"
                            >
                                <option value="">
                                    All Types
                                </option>
                                <option
                                    v-for="type in transactionTypes"
                                    :key="type"
                                    :value="type"
                                >
                                    {{ capitalize(type.replace('_', ' ')) }}
                                </option>
                            </select>
                        </div>

                        <div class="min-w-[140px]">
                            <select
                                v-model="selectedStatus"
                                class="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100"
                                @change="applyFilters"
                            >
                                <option value="">
                                    All Statuses
                                </option>
                                <option
                                    v-for="status in statuses"
                                    :key="status"
                                    :value="status"
                                >
                                    {{ capitalize(status) }}
                                </option>
                            </select>
                        </div>

                        <div class="min-w-[140px]">
                            <input
                                v-model="selectedStartDate"
                                type="date"
                                placeholder="Start Date"
                                class="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100"
                                @change="applyFilters"
                            >
                        </div>

                        <div class="min-w-[140px]">
                            <input
                                v-model="selectedEndDate"
                                type="date"
                                placeholder="End Date"
                                class="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100"
                                @change="applyFilters"
                            >
                        </div>

                        <button
                            v-if="hasActiveFilters"
                            class="px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
                            @click="clearAllFilters"
                        >
                            Clear Filters
                        </button>
                    </div>
                </div>

                <div
                    v-if="hasActiveFilters"
                    class="mt-4 flex flex-wrap items-center gap-2"
                >
                    <span class="text-sm text-gray-600 dark:text-gray-400">Active filters:</span>

                    <span
                        v-if="searchTerm"
                        class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                    >
            Search: "{{ searchTerm }}"
            <button
                class="ml-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
                @click="clearSearch"
            >
              <svg
                  class="h-3 w-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
              >
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </span>

                    <span
                        v-if="selectedType"
                        class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300"
                    >
            Type: {{ capitalize(selectedType.replace('_', ' ')) }}
            <button
                class="ml-2 text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-200"
                @click="clearTypeFilter"
            >
              <svg
                  class="h-3 w-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
              >
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </span>

                    <span
                        v-if="selectedStatus"
                        class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                    >
            Status: {{ capitalize(selectedStatus) }}
            <button
                class="ml-2 text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-200"
                @click="clearStatusFilter"
            >
              <svg
                  class="h-3 w-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
              >
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </span>

                    <span
                        v-if="selectedStartDate || selectedEndDate"
                        class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300"
                    >
            Date: {{ selectedStartDate || 'Start' }} - {{ selectedEndDate || 'End' }}
            <button
                class="ml-2 text-orange-600 hover:text-orange-800 dark:text-orange-400 dark:hover:text-orange-200"
                @click="clearDateFilter"
            >
              <svg
                  class="h-3 w-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
              >
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </span>
                </div>
            </div>
        </div>

        <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700">
            <div class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
                    <thead class="bg-gray-50 dark:bg-slate-900">
                    <tr>
                        <th
                            class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-gray-300"
                            @click="handleSort('transaction_id')"
                        >
                            <div class="flex items-center space-x-1">
                                <span>TRANSACTION ID</span>
                                <svg
                                    v-if="sortField === 'transaction_id'"
                                    class="w-4 h-4"
                                    :class="sortDirection === 'asc' ? 'transform rotate-180' : ''"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M19 9l-7 7-7-7"
                                    />
                                </svg>
                            </div>
                        </th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            USER
                        </th>
                        <th
                            class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-gray-300"
                            @click="handleSort('type')"
                        >
                            <div class="flex items-center space-x-1">
                                <span>TYPE</span>
                                <svg
                                    v-if="sortField === 'type'"
                                    class="w-4 h-4"
                                    :class="sortDirection === 'asc' ? 'transform rotate-180' : ''"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M19 9l-7 7-7-7"
                                    />
                                </svg>
                            </div>
                        </th>
                        <th
                            class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-gray-300"
                            @click="handleSort('amount')"
                        >
                            <div class="flex items-center space-x-1">
                                <span>AMOUNT</span>
                                <svg
                                    v-if="sortField === 'amount'"
                                    class="w-4 h-4"
                                    :class="sortDirection === 'asc' ? 'transform rotate-180' : ''"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M19 9l-7 7-7-7"
                                    />
                                </svg>
                            </div>
                        </th>
                        <th
                            class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-gray-300"
                            @click="handleSort('fee')"
                        >
                            <div class="flex items-center space-x-1">
                                <span>FEE</span>
                                <svg
                                    v-if="sortField === 'fee'"
                                    class="w-4 h-4"
                                    :class="sortDirection === 'asc' ? 'transform rotate-180' : ''"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M19 9l-7 7-7-7"
                                    />
                                </svg>
                            </div>
                        </th>
                        <th
                            class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-gray-300"
                            @click="handleSort('status')"
                        >
                            <div class="flex items-center space-x-1">
                                <span>STATUS</span>
                                <svg
                                    v-if="sortField === 'status'"
                                    class="w-4 h-4"
                                    :class="sortDirection === 'asc' ? 'transform rotate-180' : ''"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M19 9l-7 7-7-7"
                                    />
                                </svg>
                            </div>
                        </th>
                        <th
                            class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-gray-300"
                            @click="handleSort('created_at')"
                        >
                            <div class="flex items-center space-x-1">
                                <span>DATE</span>
                                <svg
                                    v-if="sortField === 'created_at'"
                                    class="w-4 h-4"
                                    :class="sortDirection === 'asc' ? 'transform rotate-180' : ''"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M19 9l-7 7-7-7"
                                    />
                                </svg>
                            </div>
                        </th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            ACTIONS
                        </th>
                    </tr>
                    </thead>
                    <tbody class="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-700">
                    <tr v-if="!isLoading && (!transactions.data || transactions.data.length === 0)">
                        <td
                            colspan="8"
                            class="px-6 py-12 text-center text-gray-500 dark:text-gray-400"
                        >
                            <div class="text-center">
                                <svg
                                    class="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="1"
                                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                                    />
                                </svg>
                                <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                                    No transactions found
                                </h3>
                                <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                    No transaction data available.
                                </p>
                            </div>
                        </td>
                    </tr>

                    <tr v-if="isLoading">
                        <td
                            colspan="8"
                            class="px-6 py-12 text-center"
                        >
                            <div class="flex items-center justify-center">
                                <svg
                                    class="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-600"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        class="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        stroke-width="4"
                                    />
                                    <path
                                        class="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0 1 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    />
                                </svg>
                                <span class="text-gray-600 dark:text-gray-400">Loading transactions...</span>
                            </div>
                        </td>
                    </tr>

                    <tr
                        v-for="transaction in transactions.data"
                        :key="transaction.id"
                        class="hover:bg-gray-50 dark:hover:bg-slate-700"
                    >
                        <td class="px-6 py-4 whitespace-nowrap">
                            <div class="text-sm text-gray-600 dark:text-gray-300">
                                <div class="font-medium text-gray-900 dark:text-gray-100">
                                    #{{ transaction.transaction_id }}
                                </div>
                            </div>
                        </td>

                        <td class="px-6 py-4 whitespace-nowrap">
                            <div class="flex items-center space-x-3">
                                <div class="h-10 w-10 flex-shrink-0">
                                    <div class="h-full w-full rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold text-sm shadow-md">
                                        {{ getInitials(transaction.user?.display_name || transaction.user?.name) }}
                                    </div>
                                </div>
                                <div class="min-w-0 flex-1">
                                    <div class="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                                        {{ transaction.user?.display_name || transaction.user?.name || 'N/A' }}
                                    </div>
                                    <div class="text-xs text-gray-500 dark:text-gray-400 truncate">
                                        {{ transaction.user?.email || 'N/A' }}
                                    </div>
                                </div>
                            </div>
                        </td>

                        <td class="px-6 py-4 whitespace-nowrap">
                <span
                    class="px-2.5 py-1 inline-flex text-xs leading-5 font-medium rounded-full items-center"
                    :class="getTypeBadgeClass(transaction.type)"
                >
                  <span
                      class="mr-1"
                      v-html="getTypeIcon(transaction.type)"
                  />
                  {{ transaction.type_label }}
                </span>
                        </td>

                        <td class="px-6 py-4 whitespace-nowrap">
                            <div class="text-sm text-gray-600 dark:text-gray-300">
                                <div
                                    class="font-semibold"
                                    :class="getAmountColor(transaction.amount, transaction.type)"
                                >
                                    {{ getCurrencySymbol(transaction.currency) }}{{ formatAmountWithDetails(transaction.amount, transaction.type, transaction.currency, transaction.details) }}
                                </div>
                                <div class="text-xs text-gray-400 dark:text-gray-500">
                                    {{ transaction.currency }}
                                </div>
                            </div>
                        </td>

                        <td class="px-6 py-4 whitespace-nowrap">
                            <div class="text-sm text-gray-600 dark:text-gray-300">
                                <div class="font-medium">
                                    {{ getCurrencySymbol(transaction.currency) }}{{ parseFloat(transaction.fee || 0).toFixed(getCurrencyPrecision(transaction.currency)) }}
                                </div>
                            </div>
                        </td>

                        <td class="px-6 py-4 whitespace-nowrap">
                <span
                    class="px-2.5 py-1 inline-flex text-xs leading-5 font-semibold rounded-full items-center"
                    :class="getStatusClass(transaction.status)"
                >
                  <span
                      class="mr-1"
                      v-html="getStatusIcon(transaction.status)"
                  />
                  {{ transaction.status_label }}
                </span>
                        </td>

                        <td class="px-6 py-4 whitespace-nowrap">
                            <div class="text-sm text-gray-600 dark:text-gray-300">
                                <div class="font-medium">
                                    {{ formatDate(transaction.created_at) }}
                                </div>
                                <div class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                    {{ formatTime(transaction.created_at) }}
                                </div>
                            </div>
                        </td>

                        <td class="px-6 py-4 whitespace-nowrap">
                            <button
                                class="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-slate-600 shadow-sm text-xs font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                                @click="showTransactionDetails(transaction)"
                            >
                                <svg
                                    class="h-3 w-3 mr-1"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                    />
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                    />
                                </svg>
                                Details
                            </button>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <AdminPagination
            v-if="meta.total > 0"
            :current-page="currentPage"
            :last-page="lastPage"
            :total="meta.total"
            :per-page="20"
            item-name="transactions"
            @page-change="changePage"
        />

        <div
            v-if="showDetailsModal"
            class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
            role="dialog"
            aria-modal="true"
            @click.self="closeDetailsModal"
        >
            <div class="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white dark:bg-slate-800">
                <div class="mt-3">
                    <div class="flex items-center justify-between mb-4">
                        <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100">
                            Transaction Details
                        </h3>
                        <button
                            class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                            @click="closeDetailsModal"
                        >
                            <svg
                                class="h-6 w-6"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                    </div>

                    <div
                        v-if="selectedTransaction"
                        class="space-y-4"
                    >
                        <div class="flex justify-between items-start">
                            <span class="text-sm font-medium text-gray-500 dark:text-gray-400">Transaction ID:</span>
                            <span class="text-sm text-gray-900 dark:text-gray-100 font-mono">#{{ selectedTransaction.transaction_id }}</span>
                        </div>

                        <div class="flex justify-between items-start">
                            <span class="text-sm font-medium text-gray-500 dark:text-gray-400">User:</span>
                            <div class="text-right">
                                <div class="text-sm text-gray-900 dark:text-gray-100 font-medium">
                                    {{ selectedTransaction.user?.display_name || selectedTransaction.user?.name || 'N/A' }}
                                </div>
                                <div class="text-xs text-gray-500 dark:text-gray-400">
                                    {{ selectedTransaction.user?.email || 'N/A' }}
                                </div>
                            </div>
                        </div>

                        <div class="flex justify-between items-start">
                            <span class="text-sm font-medium text-gray-500 dark:text-gray-400">Type:</span>
                            <span
                                class="px-2.5 py-1 inline-flex text-xs leading-5 font-medium rounded-full items-center"
                                :class="getTypeBadgeClass(selectedTransaction.type)"
                            >
                <span
                    class="mr-1"
                    v-html="getTypeIcon(selectedTransaction.type)"
                />
                {{ selectedTransaction.type_label }}
              </span>
                        </div>

                        <div class="flex justify-between items-start">
                            <span class="text-sm font-medium text-gray-500 dark:text-gray-400">Amount:</span>
                            <div class="text-right">
                <span
                    class="text-sm font-semibold"
                    :class="getAmountColor(selectedTransaction.amount, selectedTransaction.type)"
                >
                                    {{ getCurrencySymbol(selectedTransaction.currency) }}{{ formatAmountWithDetails(selectedTransaction.amount, selectedTransaction.type, selectedTransaction.currency, selectedTransaction.details) }}
                </span>
                                <div class="text-xs text-gray-500 dark:text-gray-400">
                                    {{ selectedTransaction.currency }}
                                </div>
                            </div>
                        </div>

                        <div class="flex justify-between items-start">
                            <span class="text-sm font-medium text-gray-500 dark:text-gray-400">Fee:</span>
                            <span class="text-sm text-gray-900 dark:text-gray-100">
                                {{ getCurrencySymbol(selectedTransaction.currency) }}{{ parseFloat(selectedTransaction.fee || 0).toFixed(getCurrencyPrecision(selectedTransaction.currency)) }}
              </span>
                        </div>

                        <div class="flex justify-between items-start">
                            <span class="text-sm font-medium text-gray-500 dark:text-gray-400">Post Balance:</span>
                            <span class="text-sm text-gray-900 dark:text-gray-100 font-medium">
                                {{ getCurrencySymbol(selectedTransaction.currency) }}{{ parseFloat(selectedTransaction.post_balance || 0).toFixed(getCurrencyPrecision(selectedTransaction.currency)) }}
              </span>
                        </div>

                        <div class="flex justify-between items-start">
                            <span class="text-sm font-medium text-gray-500 dark:text-gray-400">Status:</span>
                            <span
                                class="px-2.5 py-1 inline-flex text-xs leading-5 font-semibold rounded-full items-center"
                                :class="getStatusClass(selectedTransaction.status)"
                            >
                <span
                    class="mr-1"
                    v-html="getStatusIcon(selectedTransaction.status)"
                />
                {{ selectedTransaction.status_label }}
              </span>
                        </div>

                        <div class="flex justify-between items-start gap-4">
                            <span class="text-sm font-medium text-gray-500 dark:text-gray-400">Details:</span>
                            <div class="text-sm text-gray-900 dark:text-gray-100 text-right max-w-xs w-full">
                                <div v-if="formattedSelectedDetails.length > 0" class="space-y-2">
                                    <div
                                        v-for="item in formattedSelectedDetails"
                                        :key="item.key"
                                        class="flex justify-between gap-3 p-2 rounded-lg bg-gray-50 dark:bg-slate-700/40"
                                    >
                                        <span class="text-gray-600 dark:text-gray-400">{{ item.label }}</span>
                                        <span class="font-medium text-gray-900 dark:text-gray-100 break-all">{{ item.value }}</span>
                                    </div>
                                </div>
                                <span v-else class="break-words">{{ selectedTransaction.details || 'No additional details' }}</span>
                            </div>
                        </div>

                        <div class="flex justify-between items-start">
                            <span class="text-sm font-medium text-gray-500 dark:text-gray-400">Created At:</span>
                            <div class="text-right">
                                <div class="text-sm text-gray-900 dark:text-gray-100 font-medium">
                                    {{ formatDate(selectedTransaction.created_at) }}
                                </div>
                                <div class="text-xs text-gray-500 dark:text-gray-400">
                                    {{ formatTime(selectedTransaction.created_at) }}
                                </div>
                            </div>
                        </div>

                        <div
                            v-if="selectedTransaction.updated_at && selectedTransaction.updated_at !== selectedTransaction.created_at"
                            class="flex justify-between items-start"
                        >
                            <span class="text-sm font-medium text-gray-500 dark:text-gray-400">Updated At:</span>
                            <div class="text-right">
                                <div class="text-sm text-gray-900 dark:text-gray-100 font-medium">
                                    {{ formatDate(selectedTransaction.updated_at) }}
                                </div>
                                <div class="text-xs text-gray-500 dark:text-gray-400">
                                    {{ formatTime(selectedTransaction.updated_at) }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="mt-6 flex justify-end">
                        <button
                            class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
                            @click="closeDetailsModal"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </AdminLayout>
</template>

<style scoped>
.animate-spin {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

@media (max-width: 768px) {
    .grid-cols-1.md\:grid-cols-4 {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 640px) {
    .grid-cols-1.md\:grid-cols-4 {
        grid-template-columns: 1fr;
    }
}
</style>
